import { memo } from "react";
import { Link } from "react-router-dom";
import { MapPin, Bed, Bath, Home, Share } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useBuyer } from "@/contexts/BuyerContext";
import { APP_CONFIG } from "@/config/appConfig";
import { toast } from "@/hooks/use-toast";
import { OptimizedImage } from "@/components/ui/optimized-image";

interface PropertyCardProps {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  squareFeet: number;
  imageUrl: string;
  daysOnSite: number;
  status: "active" | "pending" | "closed" | "sold" | "expired" | "draft";
  sellerId?: string;
}

const PropertyCard = memo(({
  id,
  address,
  city,
  state,
  zip,
  price,
  bedrooms,
  bathrooms,
  squareFeet,
  imageUrl,
  daysOnSite,
  status,
  sellerId
}: PropertyCardProps) => {
  const { isBuyer } = useBuyer();
  
  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!sellerId) return;
    
    try {
      // Use edge function URL for social media crawler optimization
      const shareUrl = `${APP_CONFIG.SUPABASE_FUNCTIONS_URL}/og-property-metadata?propertyId=${id}&sellerId=${sellerId}`;
      await navigator.clipboard.writeText(shareUrl);
      
      toast({
        title: "Link copied!",
        description: "Property link has been copied to your clipboard.",
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Failed to copy link. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const statusColors = {
    active: "bg-green-600 text-white",
    pending: "bg-yellow-500 text-black",
    closed: "bg-blue-600 text-white",
    sold: "bg-blue-600 text-white",
    expired: "bg-red-600 text-white",
    draft: "bg-gray-500 text-white"
  };

  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);

  // Determine the correct route based on user type
  const propertyRoute = isBuyer ? `/buyer/properties/${id}` : `/seller/properties/${id}`;

  return (
    <Link to={propertyRoute}>
      <Card className="overflow-hidden transition-transform hover:shadow-lg hover-scale">
        <div className="relative aspect-video overflow-hidden">
          <OptimizedImage
            src={imageUrl}
            alt={`Property at ${address}`}
            className="w-full h-full object-cover"
            width={400}
            height={225}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <p className="text-white font-bold text-lg">{formattedPrice}</p>
          </div>
          <Badge 
            className={cn("absolute top-2 right-2", statusColors[status])}
            variant="outline"
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        </div>
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
            <div>
              <h3 className="font-medium leading-tight">{address}</h3>
              <p className="text-sm text-muted-foreground">{city}, {state} {zip}</p>
            </div>
          </div>
          
          <div className="flex justify-between mt-4 text-sm">
            <div className="flex items-center">
              <Bed className="h-4 w-4 mr-1" />
              <span>{bedrooms} {bedrooms === 1 ? 'Bed' : 'Beds'}</span>
            </div>
            <div className="flex items-center">
              <Bath className="h-4 w-4 mr-1" />
              <span>{bathrooms} {bathrooms === 1 ? 'Bath' : 'Baths'}</span>
            </div>
            <div className="flex items-center">
              <Home className="h-4 w-4 mr-1" />
              <span>{squareFeet.toLocaleString()} Sq Ft</span>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-border text-xs flex items-center justify-between">
            <span className="text-muted-foreground">{daysOnSite} {daysOnSite === 1 ? 'day' : 'days'} on site</span>
            {!isBuyer && sellerId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="h-7 w-7 p-0"
                title="Share property"
              >
                <Share className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
});

PropertyCard.displayName = 'PropertyCard';

export default PropertyCard;
